/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Option } from '@pbk-web/types';
import { dataAttributeProps, mouseAndKeyboardCallbackProps } from '@pbk-web/utils';
import type { FC, MutableRefObject, ReactElement, Ref, UIEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import Button, { ButtonSize, ButtonType } from '../buttons/Button';
import { ChevronIcon, ChevronType, ContextMenuIcon } from '../icons';
import Tooltip from '../tooltip/Tooltip';
import { SelectListMenu } from './SelectListMenu';

export type ContextMenuItem = {
  hide?: boolean;
  icon?: ReactElement;
  disabled?: boolean;
  disabledTooltip?: string;
  /** Stable test hook for the rendered menu item (copy-independent). */
  'data-testid'?: string;
} & (
  | {
      onClick?: () => void;
      hasDivider?: false;
    }
  | {
      hasDivider: true;
    }
) &
  (
    | {
        title: string;
      }
    | {
        customRenderer: () => ReactElement;
      }
  );

type ContextMenuProps = {
  items: ContextMenuItem[];
  className?: string;
  size?: ButtonSize;
  children?: (ref: Ref<any>, isOpen: boolean) => ReactElement | HTMLElement;
  onMenuToggle?: (isOpen: boolean) => void;
  minWidth?: string;
  /** Tooltip shown when hovering/focusing the menu trigger (hidden while open). */
  tooltip?: string;
} & ({ type?: 'button'; selectedButtonTitle: string; buttonType?: ButtonType } | { type?: 'icon'; selectedButtonTitle?: string });

const ContextMenu: FC<ContextMenuProps> = (props) => {
  const { items, className, type = 'icon', selectedButtonTitle, size = ButtonSize.M, children, onMenuToggle, minWidth, tooltip } = props;
  const buttonType = type === 'button' && 'buttonType' in props ? (props.buttonType ?? ButtonType.PRIMARY) : ButtonType.PRIMARY;
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('common');
  const menuId = useMemo(() => uuid(), []);

  const dataAttrs = dataAttributeProps(props);

  useEffect(() => {
    onMenuToggle?.(open);
  }, [onMenuToggle, open]);

  const toggleVisibility = (e: UIEvent<HTMLElement> | null = null): void => {
    e?.preventDefault();
    e?.stopPropagation();
    setOpen((prev) => !prev);
  };

  const onItemClick = (itemIndex: string): void => {
    toggleVisibility();
    const item = items.filter((item) => !item.hide)[parseInt(itemIndex, 10)];
    if (item && 'onClick' in item) {
      item.onClick?.();
    }
  };

  const options = useMemo(
    () =>
      items
        .filter((item) => !item.hide)
        .map((item, i) => ({ ...item, id: i.toString(), text: 'title' in item ? item.title : '', value: i.toString() })),
    [items],
  );

  const CustomListRenderer = useMemo(
    () =>
      function ContextMenuListRenderer(item: Option<string, string | number> & ContextMenuItem) {
        const { id } = item;

        if ('customRenderer' in item) return item.customRenderer();
        return (
          <Tooltip text={item.disabled ? item.disabledTooltip : ''}>
            {(tooltip) => (
              <div key={id} data-testid={item['data-testid']} className="text-pbk-16 flex items-center" {...tooltip}>
                {item.icon && (
                  <div className={`-ml-2 flex shrink-0 items-center pr-2 ${item.disabled ? 'cursor-default opacity-50' : ''}`}>{item.icon}</div>
                )}
                {item.title}
              </div>
            )}
          </Tooltip>
        );
      },
    [],
  );

  if (!options.length) {
    return null;
  }

  return (
    <SelectListMenu
      id={`context-menu-${menuId}`}
      onBlur={() => setOpen(false)}
      width={minWidth ?? 'min-w-64!'}
      options={options}
      isOpen={open}
      onClick={(option) => onItemClick(option.id)}
      customListItemRenderer={CustomListRenderer}
      style={{ maxWidth: 'none!important' }}
    >
      {(triggerProps) => (
        <div {...triggerProps} className={`relative ${className}`} {...dataAttrs}>
          <Tooltip text={open ? '' : tooltip}>
            {({ tabIndex: _tabIndex, ...tooltipProps }) => (
              // Separate anchor so the tooltip ref doesn't clash with the menu trigger ref
              <div {...tooltipProps}>
                {type === 'icon' && !children && (
                  <div
                    aria-haspopup="true"
                    aria-controls={`context-menu-${menuId}`}
                    aria-expanded={open}
                    aria-label={t('aria-label.context-menu')}
                    data-testid="context-menu"
                    {...dataAttributeProps(props)}
                    className="hover:bg-greyscale-15 relative cursor-pointer rounded-md p-1 transition-colors select-none"
                    {...mouseAndKeyboardCallbackProps(toggleVisibility)}
                  >
                    <ContextMenuIcon className={` ${size === ButtonSize.S ? 'h-3 w-3' : 'h-4 w-4'}`} />
                  </div>
                )}
                {type === 'icon' && !!children && (
                  <div
                    aria-haspopup="true"
                    aria-controls={`context-menu-${menuId}`}
                    aria-expanded={open}
                    aria-label={t('aria-label.context-menu')}
                    data-testid="context-menu"
                    {...dataAttributeProps(props)}
                    {...mouseAndKeyboardCallbackProps(toggleVisibility)}
                  >
                    {children(triggerProps.ref as MutableRefObject<HTMLElement>, open)}
                  </div>
                )}
                {type === 'button' && (
                  <Button
                    aria-haspopup="true"
                    aria-controls={`context-menu-${menuId}`}
                    aria-expanded={open}
                    aria-label={t('aria-label.context-menu')}
                    type={buttonType}
                    size={size}
                    onClick={toggleVisibility}
                  >
                    {selectedButtonTitle} <ChevronIcon type={ChevronType.DOWN} className="ml-2 h-5 w-5" />
                  </Button>
                )}
              </div>
            )}
          </Tooltip>
        </div>
      )}
    </SelectListMenu>
  );
};

export default ContextMenu;
